import { api } from "@api";
import { staticAssetsApi } from "@api/static-assets";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from "react-redux";
import { aiSlidesSlice } from "./ai-slides";
import { dialogSlice } from "./dialogs";
import { filesHistory } from "./files-history";
import { globalSlice } from "./global-slice";
import { hubspotIntegrationSlice } from "./hubspot-integration";
import { aiCardAlignment } from "./middleware/ai-card-alignment";
import { onboardingRefreshOnMutation } from "./middleware/onboarding-refresh";
import { sentryRtkErrorLogger } from "./middleware/sentry-rtk-error-logger";
import { shareDialogSlice } from "./share-dialog";
import { storySlice } from "./story";

// Combine all reducers
const rootReducer = combineReducers({
  [api.reducerPath]: api.reducer,
  story: storySlice.reducer,
  shareDialog: shareDialogSlice.reducer,
  hubspotIntegration: hubspotIntegrationSlice.reducer,
  global: globalSlice.reducer,
  aiSlides: aiSlidesSlice.reducer,
  filesHistory: filesHistory.reducer,
  dialogs: dialogSlice.reducer,
  [staticAssetsApi.reducerPath]: staticAssetsApi.reducer,
});

const resettableReducer = (
  state: ReturnType<typeof rootReducer> | undefined,
  action: { type: "store/reset" },
): ReturnType<typeof rootReducer> => {
  if (action.type === "store/reset") {
    // Reset all state to initial values
    state = undefined;
  }
  return rootReducer(state, action);
};

export const store = configureStore({
  reducer: resettableReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).concat(
      api.middleware,
      staticAssetsApi.middleware,
      // Cards the agent writes are top-aligned (see the module).
      aiCardAlignment,
      // Must run AFTER the RTK Query middlewares so the rejected action is
      // fully decorated with `meta.baseQueryMeta` before we inspect it.
      sentryRtkErrorLogger,
      onboardingRefreshOnMutation,
    ),
});
// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const resetStore = () => ({ type: "store/reset" });
